import type { SimpleChanges } from '@angular/core';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { FormBuilder, FormGroup } from '@angular/forms';
import { NzTableQueryParams } from 'ng-zorro-antd/table';
import { Subject, distinctUntilChanged, takeUntil } from 'rxjs';

type AgLikeColDef = {
  headerName?: string;
  field?: string;
  width?: number;
  sortable?: boolean;
  cellClick?: (ev: MouseEvent, row: { value: any; data: any; colDef: AgLikeColDef }) => void;
  enlace?: boolean;
  html?: string | ((row: any) => string);
  linkEnabledField?: string;
  linkEnabled?: (row: any) => boolean;
};

type NormalizedCol = {
  headerName: string;
  field: string;
  widthPx?: number;
  sortable: boolean;
  sortFn: (a: any, b: any) => number;
  cellClick?: AgLikeColDef['cellClick'];
  enlace?: AgLikeColDef['enlace'];
  html?: AgLikeColDef['html'];
  linkEnabledField?: AgLikeColDef['linkEnabledField'];
  linkEnabled?: AgLikeColDef['linkEnabled'];
};

@Component({ standalone: false, selector: 'app-shared-base-new-grid',
  templateUrl: './new-grid.component.html',
  styleUrls: ['./new-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BaseNewGridComponent implements OnInit, OnChanges, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  readonly trackByRow = (index: number, row: any): string | number => row?.id ?? row?.key ?? index;

  @Input() data: any[] = [];
  @Input() busquedaActiva: boolean = true;
  @Input() columnDefs: AgLikeColDef[] = [];

  @Input() loading: any;
  @Input() styleTable: Record<string, string> = {};

  get isLoading(): boolean {
    if (typeof this.loading === 'boolean') return this.loading;
    const v = this.loading?.get?.isLoading?.();
    return typeof v === 'boolean' ? v : false;
  }

  @Input() placeholderSearch = 'Buscar...';
  @Input() showResults = true;

  // opcionales (no rompen la llamada mínima)
  @Input() showCreate = false;
  @Input() canCrear = true;
  @Input() createLabel = 'Nuevo';
  @Output() readonly onCreate = new EventEmitter<void>();

  @Output() readonly onRefrescarTabla = new EventEmitter<void>();
  @Output() readonly onSelected = new EventEmitter<any>();
  @Output() readonly onQueryParams = new EventEmitter<NzTableQueryParams>();

  readonly formSearchText: FormGroup;

  normalizedCols: NormalizedCol[] = [];
  displayData: any[] = [];

  constructor(
    private readonly fb: FormBuilder,
    private readonly sanitizer: DomSanitizer
  ) {
    this.formSearchText = this.fb.group({ searchText: [''] });
  }

  ngOnInit(): void {
    this.formSearchText.controls['searchText'].valueChanges
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => this.recomputeDisplayData());

    this.recomputeCols();
    this.recomputeDisplayData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['columnDefs']) this.recomputeCols();
    if (changes['data']) this.recomputeDisplayData();
  }

  refrescarTabla(): void {
    this.onRefrescarTabla.emit();
  }

  ejecutarBusqueda(): void {
    this.recomputeDisplayData();
  }

  renderCell(col: NormalizedCol, row: any): SafeHtml | string {
    if (col?.enlace) {
      if (typeof col.html === 'function') return col.html(row);
      return col.html ?? '';
    }

    return this.getCellValue(row, col.field) ?? '';
  }

  isLinkEnabled(col: NormalizedCol, row: any): boolean {
    if (!(col?.enlace ?? false)) return false;

    if (typeof col.linkEnabled === 'function') {
      return !!col.linkEnabled(row);
    }

    if (col.linkEnabledField) {
      return !!this.getCellValue(row, col.linkEnabledField);
    }

    const href = this.getCellValue(row, col.field);
    return href !== null && href !== undefined && String(href).trim() !== '';
  }

  handleLinkClick(ev: MouseEvent, col: any, row: Record<string, string>) {
    if (!this.isLinkEnabled(col, row)) {
      ev.preventDefault();
      ev.stopPropagation();
      return;
    }

    if (typeof col.cellClick === 'function') {
      ev.preventDefault();
      ev.stopPropagation();
      col.cellClick(ev, {
        value: this.getCellValue(row, col.field),
        data: row,
        colDef: col,
      });
    }
  }

  private recomputeCols(): void {
    const defs = Array.isArray(this.columnDefs) ? this.columnDefs : [];
    this.normalizedCols = defs
      .filter(d => !!d?.field)
      .map(d => {
        const field = (d.field || '').trim();
        const headerName = (d.headerName || field).trim();

        const sortable = !!d.sortable;

        return {
          headerName,
          field,
          widthPx: typeof d.width === 'number' ? d.width : undefined,
          sortable,
          cellClick: d.cellClick,
          html: d?.html,
          enlace: d?.enlace,
          linkEnabledField: d?.linkEnabledField,
          linkEnabled: d?.linkEnabled,
          sortFn: (a: any, b: any) => {
            const av = this.getCellValue(a, field);
            const bv = this.getCellValue(b, field);

            // null/undefined al final
            if ((av === null || av === undefined) && (bv === null || bv === undefined)) return 0;
            if (av === null || av === undefined) return 1;
            if (bv === null || bv === undefined) return -1;

            // números
            const an = Number(av);
            const bn = Number(bv);
            const bothNumeric = !Number.isNaN(an) && !Number.isNaN(bn) && (typeof av !== 'boolean') && (typeof bv !== 'boolean');
            if (bothNumeric) return an - bn;

            // strings
            return String(av).localeCompare(String(bv), undefined, { sensitivity: 'base' });
          }
        };
      });
  }

  private recomputeDisplayData(): void {
    const termRaw = this.formSearchText?.controls?.['searchText']?.value ?? '';
    const term = this.normalizarBusqueda(termRaw);

    const source = Array.isArray(this.data) ? this.data : [];

    if (!term) {
      this.displayData = source;
      return;
    }

    const fields = this.normalizedCols.map(c => c.field);

    this.displayData = source.filter(row => {
      for (const f of fields) {
        const v = this.getCellValue(row, f);
        if (v === null || v === undefined) continue;
        if (this.normalizarBusqueda(v).includes(term)) return true;
      }
      return false;
    });
  }

  private normalizarBusqueda(value: any): string {
    return String(value ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase();
  }

  getCellValue(row: any, fieldPath: string): any {
    if (!row || !fieldPath) return '';
    // soporte "a.b.c"
    const parts = fieldPath.split('.');
    let cur = row;
    for (const p of parts) {
      if (cur === null || cur === undefined) return '';
      cur = cur[p];
    }
    return cur ?? '';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
